import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Spinner } from '../components/ui';

function formatDate(d) {
  if (!d) return 'Not recorded';
  return new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' });
}

function certStatus(cert) {
  if (!cert?.has) return { label:'Not recorded', color:'#dc2626', badge:'badge-bad' };
  if (!cert.expiry_date) return { label:'No expiry date', color:'#d97706', badge:'badge-warn' };
  const days = Math.ceil((new Date(cert.expiry_date) - new Date()) / 86400000);
  if (days < 0)  return { label:`Expired ${formatDate(cert.expiry_date)}`, color:'#dc2626', badge:'badge-bad' };
  if (days < 30) return { label:`Expires ${formatDate(cert.expiry_date)} (${days}d)`, color:'#d97706', badge:'badge-warn' };
  return { label:`Valid until ${formatDate(cert.expiry_date)}`, color:'#16a34a', badge:'badge-ok' };
}

const CERT_LABELS = {
  epc:'EPC (Energy Performance Certificate)', gas_safety:'Gas Safety Certificate',
  eicr:'EICR (Electrical Installation)', smoke_alarm:'Smoke Alarm Check',
  co_alarm:'CO Alarm Check', legionella:'Legionella Risk Assessment',
};

const WIZARD_STORAGE_KEY = 'br_wizard_state_v1';

export default function ViewReport() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`/api/report/view/${token}`)
      .then(r => {
        if (r.status === 402) throw new Error('payment_required');
        if (r.status === 410) throw new Error('expired');
        if (!r.ok) throw new Error('not_found');
        return r.json();
      })
      .then(data => { setReport(data); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [token]);

  useEffect(() => {
    // If coming from Stripe success, poll for payment confirmation
    const sessionID = searchParams.get('session_id');
    if (sessionID && !report) {
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        const r = await fetch(`/api/report/view/${token}`);
        if (r.ok) {
          const data = await r.json();
          setReport(data);
          setLoading(false);
          clearInterval(poll);
        }
        if (attempts > 15) clearInterval(poll);
      }, 2000);
      return () => clearInterval(poll);
    }
  }, [token, searchParams]);

  useEffect(() => {
    if (!report) return;
    try { window.localStorage.removeItem(WIZARD_STORAGE_KEY); } catch {}
  }, [report]);

  if (loading) return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100vh', gap:16, fontFamily:'sans-serif' }}>
      <div style={{ fontSize:32 }}>🎉</div>
      <p style={{ fontSize:18, fontWeight:300 }}>Setting up your report…</p>
      <Spinner />
      <p style={{ fontSize:13, color:'#6b7280' }}>This usually takes just a few seconds</p>
    </div>
  );

  if (error === 'payment_required') return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100vh', fontFamily:'sans-serif', padding:20 }}>
      <p style={{ fontSize:32, marginBottom:16 }}>🔒</p>
      <p style={{ fontSize:20, fontWeight:300, marginBottom:8 }}>Payment required</p>
      <p style={{ color:'#6b7280', marginBottom:24 }}>Purchase this report to view it.</p>
      <a href="/report/buy" style={{ background:'#1B4332', color:'#fff', padding:'12px 24px', borderRadius:8, textDecoration:'none', fontSize:15 }}>
        Buy report — £19.99 →
      </a>
    </div>
  );

  if (error === 'expired') return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100vh', fontFamily:'sans-serif', padding:20 }}>
      <p style={{ fontSize:32, marginBottom:16 }}>⏰</p>
      <p style={{ fontSize:20, fontWeight:300, marginBottom:8 }}>Report link expired</p>
      <p style={{ color:'#6b7280', marginBottom:24 }}>This report link was valid for 30 days.</p>
      <a href="/report/buy" style={{ background:'#1B4332', color:'#fff', padding:'12px 24px', borderRadius:8, textDecoration:'none', fontSize:15 }}>
        Generate a new report →
      </a>
    </div>
  );

  if (error === 'not_found' || (error && !['payment_required','expired'].includes(error))) return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100vh', fontFamily:'sans-serif', padding:20 }}>
      <p style={{ fontSize:32, marginBottom:16 }}>🔍</p>
      <p style={{ fontSize:20, fontWeight:300, marginBottom:8 }}>Report not found</p>
      <p style={{ color:'#6b7280', marginBottom:24 }}>This report link is invalid or has expired.</p>
      <a href="/report/buy" style={{ background:'#1B4332', color:'#fff', padding:'12px 24px', borderRadius:8, textDecoration:'none', fontSize:15 }}>
        Buy a report — £19.99 →
      </a>
    </div>
  );

  if (!report) return null;

  const pd = report.property_data || {};
  const rra = pd.rra || {};
  const certs = pd.certs || {};
  const tenants = pd.tenants || [];
  const generatedDate = new Date(pd.generated_at || report.created_at).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' });

  const allValid = Object.entries(certs).every(([k, c]) => {
    if (k === 'gas_safety' && !pd.has_gas) return true;
    return c.has && c.expiry_date && new Date(c.expiry_date) > new Date();
  }) && (!pd.deposit?.has_deposit || (pd.deposit.scheme !== 'none' && !!pd.deposit.protected_date && !!pd.deposit.prescribed_info_given));

  return (
    <div id="report">
      <style>{`
        @media print {
          body { margin: 0; }
          .no-print { display: none !important; }
        }
        body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #1a1a18; background: #fff; margin: 0; }
        #report { max-width: 800px; margin: 0 auto; padding: 40px; }
        .header { border-bottom: 3px solid #1a1a18; padding-bottom: 24px; margin-bottom: 32px; display: flex; justify-content: space-between; align-items: flex-start; }
        .logo { font-size: 22px; font-weight: 300; }
        .logo em { color: #1B4332; font-style: italic; }
        h1 { font-size: 28px; font-weight: 300; margin: 0 0 4px; }
        h2 { font-size: 16px; font-weight: 700; margin: 0 0 16px; padding-bottom: 8px; border-bottom: 1px solid #e5e7eb; text-transform: uppercase; letter-spacing: .05em; color: #374151; }
        .section { margin-bottom: 32px; }
        .meta-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; background: #f9fafb; border-radius: 8px; padding: 20px; margin-bottom: 32px; }
        .meta-item label { font-size: 11px; text-transform: uppercase; letter-spacing: .05em; color: #6b7280; display: block; margin-bottom: 2px; }
        .meta-item span { font-size: 14px; font-weight: 500; }
        table { width: 100%; border-collapse: collapse; font-size: 13px; }
        th { text-align: left; padding: 8px 12px; background: #f3f4f6; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: .05em; }
        td { padding: 10px 12px; border-bottom: 1px solid #f3f4f6; }
        .badge { display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 11px; font-weight: 600; }
        .badge-ok   { background: #dcfce7; color: #16a34a; }
        .badge-warn { background: #fef3c7; color: #d97706; }
        .badge-bad  { background: #fee2e2; color: #dc2626; }
        .summary-ok  { background: #f0fdf4; border: 1px solid #86efac; border-radius: 8px; padding: 16px 20px; margin-bottom: 24px; }
        .summary-bad { background: #fef2f2; border: 1px solid #fca5a5; border-radius: 8px; padding: 16px 20px; margin-bottom: 24px; }
        .disclaimer { background: #fffbeb; border: 1px solid #fcd34d; border-radius: 8px; padding: 12px 16px; margin-top: 24px; font-size: 12px; color: #92400e; }
        .footer { margin-top: 48px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #9ca3af; display: flex; justify-content: space-between; }
        .toolbar { background: #1a1a18; color: #fff; padding: 12px 24px; display: flex; justify-content: space-between; align-items: center; position: fixed; top: 0; left: 0; right: 0; z-index: 100; }
        .toolbar button { background: #fff; color: #1a1a18; border: none; padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer; margin-left: 8px; font-size: 13px; }
        .toolbar button.secondary { background: rgba(255,255,255,.2); color: #fff; }
      `}</style>

      <div className="toolbar no-print">
        <span style={{ fontWeight:300, fontSize:16 }}>Landlord<em style={{ color:'#6ee7b7' }}>Comply</em> — Compliance Report</span>
        <div>
          <button className="secondary" onClick={() => window.location.href='/report/buy'}>← New report</button>
          <button onClick={() => window.print()}>🖨️ Print / Save PDF</button>
        </div>
      </div>
      <div className="no-print" style={{ height:52 }} />

      {/* Header */}
      <div className="header">
        <div>
          <div className="logo">Landlord<em>Comply</em></div>
          <h1 style={{ marginTop:8 }}>Compliance Report</h1>
          <p style={{ margin:0, fontSize:14, color:'#6b7280' }}>{pd.address}, {pd.city}, {pd.postcode}</p>
        </div>
        <div style={{ textAlign:'right', fontSize:12, color:'#6b7280' }}>
          <p style={{ margin:'0 0 4px', fontWeight:600 }}>Generated: {generatedDate}</p>
          <p style={{ margin:0 }}>Report expires: {formatDate(report.expires_at)}</p>
        </div>
      </div>

      {/* Property meta */}
      <div className="meta-grid">
        <div className="meta-item"><label>Property type</label><span>{pd.property_type?.replace('_',' ') || '—'}</span></div>
        <div className="meta-item"><label>Bedrooms</label><span>{pd.bedrooms || '—'}</span></div>
        <div className="meta-item"><label>Has gas</label><span>{pd.has_gas ? 'Yes' : 'No'}</span></div>
        <div className="meta-item"><label>Water heating</label><span>{pd.water_heating?.replace('_',' ') || '—'}</span></div>
        <div className="meta-item"><label>Postcode</label><span>{pd.postcode || '—'}</span></div>
        <div className="meta-item"><label>Report ID</label><span style={{ fontSize:11, fontFamily:'monospace' }}>{token?.slice(0,8)}…</span></div>
      </div>

      {/* Compliance summary */}
      {allValid ? (
        <div className="summary-ok">
          <p style={{ fontWeight:700, color:'#15803d', marginBottom:4 }}>✅ Property appears compliant as of {generatedDate}</p>
          <p style={{ fontSize:13, color:'#166534' }}>All recorded certificates are valid. This report can be used as supporting evidence of compliance.</p>
        </div>
      ) : (
        <div className="summary-bad">
          <p style={{ fontWeight:700, color:'#dc2626', marginBottom:4 }}>⚠️ Compliance gaps identified</p>
          <p style={{ fontSize:13, color:'#991b1b' }}>One or more required certificates are missing or expired. Action is required.</p>
        </div>
      )}

      {/* Certificates */}
      <div className="section">
        <h2>Certificates & Safety Checks</h2>
        <table>
          <thead><tr><th>Certificate</th><th>Expiry date</th><th>Status</th></tr></thead>
          <tbody>
            {Object.entries(CERT_LABELS).map(([key, label]) => {
              if (key === 'gas_safety' && !pd.has_gas) return null;
              const s = certStatus(certs[key]);
              return (
                <tr key={key}>
                  <td style={{ fontWeight:500 }}>{label}</td>
                  <td>{certs[key]?.expiry_date ? formatDate(certs[key].expiry_date) : '—'}</td>
                  <td><span className={`badge ${s.badge}`}>{s.label}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Tenants */}
      {tenants.filter(t => t.name).length > 0 && (
        <div className="section">
          <h2>Tenants & Right to Rent</h2>
          <table>
            <thead><tr><th>Name</th><th>Right to Rent check</th><th>Tenancy start</th></tr></thead>
            <tbody>
              {tenants.filter(t => t.name).map((t, i) => (
                <tr key={i}>
                  <td style={{ fontWeight:500 }}>{t.name}</td>
                  <td>{t.check_date ? <span className="badge badge-ok">Checked {formatDate(t.check_date)}</span> : <span className="badge badge-bad">Not recorded</span>}</td>
                  <td>{formatDate(t.tenancy_start)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Deposit Protection */}
      {pd.deposit?.has_deposit && (() => {
        const dep = pd.deposit;
        const protectedOk = dep.scheme !== 'none' && !!dep.protected_date;
        const infoOk = !!dep.prescribed_info_given;
        const compliant = protectedOk && infoOk;
        const schemeLabels = { dps:'Deposit Protection Service (DPS)', mydeposits:'mydeposits', tds:'Tenancy Deposit Scheme (TDS)', none:'Not protected' };
        return (
          <div className="section">
            <h2>Deposit Protection</h2>
            <table>
              <thead><tr><th>Item</th><th>Detail</th><th>Status</th></tr></thead>
              <tbody>
                <tr>
                  <td style={{ fontWeight:500 }}>Deposit amount</td>
                  <td>{dep.amount ? `£${Number(dep.amount).toFixed(2)}` : '—'}</td>
                  <td>—</td>
                </tr>
                <tr>
                  <td style={{ fontWeight:500 }}>Protection scheme</td>
                  <td>{schemeLabels[dep.scheme] || dep.scheme}</td>
                  <td>{protectedOk ? <span className="badge badge-ok">Protected</span> : <span className="badge badge-bad">Not protected</span>}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight:500 }}>Date protected</td>
                  <td>{dep.protected_date ? formatDate(dep.protected_date) : '—'}</td>
                  <td>{dep.protected_date ? <span className="badge badge-ok">Recorded</span> : <span className="badge badge-bad">Not recorded</span>}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight:500 }}>Prescribed Information served</td>
                  <td>—</td>
                  <td>{infoOk ? <span className="badge badge-ok">✓ Served</span> : <span className="badge badge-bad">Not recorded</span>}</td>
                </tr>
              </tbody>
            </table>
            {!compliant && (
              <div style={{ marginTop:12, padding:'10px 14px', background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:6 }}>
                <p style={{ margin:0, fontSize:12, color:'#991b1b', fontWeight:600 }}>
                  ⚠️ Deposit protection incomplete — deposits must be protected in a government-approved scheme and Prescribed Information served within 30 days. A tenant can claim 1–3× the deposit amount for non-compliance.
                </p>
              </div>
            )}
          </div>
        );
      })()}

      {/* RRA Compliance */}
      <div className="section">
        <h2>Renters' Rights Act 2025 Compliance</h2>
        <table>
          <thead>
            <tr><th>Requirement</th><th>Status</th><th>Date</th></tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ fontWeight:500 }}>Information Sheet sent to all tenants</td>
              <td>
                {rra.info_sheet_sent
                  ? <span className="badge badge-ok">✓ Sent</span>
                  : <span className="badge badge-bad">Not recorded</span>}
              </td>
              <td>{rra.info_sheet_date || '—'}</td>
            </tr>
            <tr>
              <td style={{ fontWeight:500 }}>Section 13 rent review notice</td>
              <td>
                {rra.has_rent_review
                  ? <span className="badge badge-ok">✓ Served</span>
                  : <span className="badge" style={{ background:'#f3f4f6', color:'#6b7280' }}>Not applicable</span>}
              </td>
              <td>{rra.rent_review_date || '—'}</td>
            </tr>
            <tr>
              <td style={{ fontWeight:500 }}>Pet request(s) received</td>
              <td>
                {rra.has_pet_request
                  ? <span className={`badge ${rra.pet_decision === 'approved' ? 'badge-ok' : rra.pet_decision === 'declined' ? 'badge-warn' : 'badge-warn'}`}>
                      {rra.pet_decision === 'approved' ? '✓ Approved' : rra.pet_decision === 'declined' ? 'Declined' : 'Pending'}
                    </span>
                  : <span className="badge" style={{ background:'#f3f4f6', color:'#6b7280' }}>None received</span>}
              </td>
              <td>—</td>
            </tr>
          </tbody>
        </table>
        {!rra.info_sheet_sent && (
          <div style={{ marginTop:12, padding:'10px 14px', background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:6 }}>
            <p style={{ margin:0, fontSize:12, color:'#991b1b', fontWeight:600 }}>
              ⚠️ Information Sheet not recorded as sent — mandatory deadline 31 May 2026. Fine up to £7,000.
            </p>
          </div>
        )}
      </div>

      <div className="disclaimer">
        <strong>Important:</strong> This report is based on information provided by the user and is for reference purposes only.
        It does not constitute legal advice. LandlordComply accepts no liability for inaccuracies in user-entered data.
        Always verify compliance with a qualified professional.
      </div>

      <div className="footer">
        <span>LandlordComply — UK Compliance Hub — landlordcomply.uk</span>
        <span>Report generated {generatedDate}</span>
      </div>
    </div>
  );
}